import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/auth/auth.guard';
import { AccountCompletenessGuard } from '@core/guards/account-completeness.guard';
import { LandingGuard } from '@core/guards/landing.guard';
import { MessagesAllowedGuard } from '@core/guards/messages-allowed.guard';
import { MvpRestrictedGuard } from '@core/guards/mvp-restricted.guard';
import { RoleGuard } from '@core/guards/role.guard';
import { CustodianRouteSegment } from '@modules/custodian/enums/custodian-route-segment.enum';
import { ContactUsComponent } from '@modules/custodian/pages/contact-us-page/contact-us-page.component';
import { AccountCompleteness, UserRole } from '@wtax/data-angular';
import { AuthCallbackComponent } from '../app/components/auth-callback/auth-callback.component';
import { RouteData } from './shared/enums/route-data.enum';
import { RouteSegment } from './shared/enums/route-segment.enum';

export const routes: Routes = [
  {
    path: RouteSegment.AuthCallback,
    component: AuthCallbackComponent,
  },
  {
    path: RouteSegment.Root,
    canActivate: [AuthGuard],
    children: [
      {
        path: RouteSegment.Root,
        pathMatch: 'full',
        children: [],
        canActivate: [LandingGuard],
      },
      {
        path: RouteSegment.Dashboard,
        loadChildren: () => import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
        canLoad: [MvpRestrictedGuard],
        canActivate: [AccountCompletenessGuard, RoleGuard, MvpRestrictedGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER, UserRole.FUND_MANAGER, UserRole.BENEFICIAL_OWNER],
        },
      },
      {
        path: RouteSegment.Reporting,
        loadChildren: () => import('./modules/reporting-page/reporting-page.module').then((m) => m.ReportingPageModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER, UserRole.FUND_MANAGER, UserRole.BENEFICIAL_OWNER],
        },
      },
      {
        path: RouteSegment.Insights,
        loadChildren: () => import('./modules/insights-page/insights-page.module').then((m) => m.InsightsPageModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER, UserRole.FUND_MANAGER],
        },
      },
      {
        path: RouteSegment.ClientManagement,
        loadChildren: () => import('@modules/client-management/client-management.module').then((m) => m.ClientManagementModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: { [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED, [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER] },
      },
      {
        path: RouteSegment.FundManagement,
        loadChildren: () => import('@modules/fund-management/fund-management.module').then((m) => m.FundManagementModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: { [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED, [RouteData.AllowedRoles]: [UserRole.FUND_MANAGER] },
      },
      {
        path: RouteSegment.AccountSetup,
        loadChildren: () => import('./modules/account-setup/account-setup.module').then((m) => m.AccountSetupModule),
        canActivate: [AccountCompletenessGuard],
        data: { [RouteData.RequiredCompleteness]: AccountCompleteness.INCOMPLETE },
      },
      {
        path: RouteSegment.Payments,
        loadChildren: () => import('./modules/payments/payments.module').then((m) => m.PaymentsModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER, UserRole.FUND_MANAGER, UserRole.BENEFICIAL_OWNER],
        },
      },
      {
        path: RouteSegment.MyAccount,
        loadChildren: () => import('./modules/profile/profile.module').then((m) => m.ProfileModule),
        canActivate: [AccountCompletenessGuard],
        data: { [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED },
      },
      {
        path: RouteSegment.YourTasks,
        loadChildren: () => import('./modules/your-tasks/your-tasks.module').then((m) => m.YourTasksModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.BENEFICIAL_OWNER, UserRole.INVESTOR],
        },
      },
      {
        path: RouteSegment.Messages,
        loadChildren: () => import('./modules/message-list/message-list.module').then((m) => m.MessageListModule),
        canActivate: [AccountCompletenessGuard, MessagesAllowedGuard],
        data: { [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED },
      },
      {
        path: RouteSegment.TaxFormPrePopulation,
        loadChildren: () =>
          import('./modules/custodian/tax-form-pre-population/tax-form-pre-population.module').then((m) => m.TaxFormPrePopulationModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.ContactUs,
        component: ContactUsComponent,
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: CustodianRouteSegment.AddOnTaxServices,
        loadChildren: () =>
          import('./modules/custodian/add-on-tax-services/add-on-tax-services.module').then((m) => m.AddOnTaxServicesModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.Custodian,
        loadChildren: () => import('./modules/custodian/custodian.module').then((m) => m.CustodianModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: CustodianRouteSegment.QueryManagement,
        loadChildren: () => import('./modules/custodian/query-management/query-management.module').then((m) => m.QueryManagementModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: CustodianRouteSegment.TaxRiskReporting,
        loadChildren: () =>
          import('./modules/custodian/tax-risk-reporting/tax-risk-reporting.module').then((m) => m.TaxRiskReportingModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.HealthChecksOnBeneficialOwners,
        loadChildren: () =>
          import(
            './modules/custodian/tax-risk-reporting/health-checks-on-beneficial-owners/health-checks-on-beneficial-owners.module'
          ).then((m) => m.HealthChecksOnBeneficialOwnersModule),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.HealthChecksOnTaxRateTables,
        loadChildren: () =>
          import('./modules/custodian/tax-risk-reporting/health-checks-on-tax-rate-tables/health-checks-on-tax-rate-tables.module').then(
            (m) => m.HealthChecksOnTaxRateTablesModule
          ),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.TaxReclaimAccrualChecks,
        loadChildren: () =>
          import('./modules/custodian/tax-risk-reporting/tax-reclaim-accrual-checks/tax-reclaim-accrual-checks.module').then(
            (m) => m.TaxReclaimAccrualChecksModule
          ),
        canActivate: [AccountCompletenessGuard, RoleGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.CUSTODIAN],
        },
      },
      {
        path: RouteSegment.DocumentManager,
        loadChildren: () => import('./modules/document-request-batches/document-manager.module').then((m) => m.DocumentManagerModule),
        canLoad: [MvpRestrictedGuard],
        canActivate: [AccountCompletenessGuard, RoleGuard, MvpRestrictedGuard],
        data: {
          [RouteData.RequiredCompleteness]: AccountCompleteness.COMPLETED,
          [RouteData.AllowedRoles]: [UserRole.WEALTH_MANAGER, UserRole.FUND_MANAGER, UserRole.BENEFICIAL_OWNER],
        },
      },
      {
        path: RouteSegment.DocumentRequestBatches,
        loadChildren: () =>
          import('./modules/document-request-batches/document-request-batches.module').then((m) => m.DocumentRequestBatchesModule),
        canActivate: [AuthGuard],
        data: { [RouteData.HideNavItems]: true },
      },
    ],
  },
  { path: '**', redirectTo: RouteSegment.Root },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
